import { useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { Box, Button, Dialog, DialogContent, Grid, Typography } from '@mui/material';
import { BsEye, BsX } from 'react-icons/bs';
import { PiPaperPlaneTilt } from 'react-icons/pi';

import { MetaTags, useQuery } from '@redwoodjs/web';

import Loader from 'src/components/Loader/Loader';
import { showErrorToast } from 'src/components/Toasts/toast';
import ViewPDFModal from 'src/components/ViewPDFModal';
import { CommissionType } from 'src/features/finance/commissionStatement/constants/commissionType';
import { BillingDetails } from 'src/features/finance/sepaDirectDebits/components';
import {
    GET_COMMISSION_STATEMENT,
    GET_COMMISSION_STATEMENT_PDF,
} from 'src/graphql/queries/getCommissionStatements.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { FormatBillingDetails } from 'src/lib/formatters';
import { CommissionStatementDetails, StatementStatusType } from 'src/types/commission-statement';
import { onError } from 'src/utils/utils';

import OrderSumCard from '../AddMerchantPage/components/OrderSumCard';

import CommissionsItemsTable from './CommissionItemsTable';
import getCommissionTypeHeading from './getCommissionTypeHeading';
import { SendCommissionStatementModal } from './SendCommissionStatementModal';
import StatementDetails from './StatementDetails';

type ViewCommissionStatementProps = {
    id: number;
    commissionType: CommissionType;
};

const ViewCommissionStatementPage = ({ id, commissionType }: ViewCommissionStatementProps) => {
    const [openSendModal, setOpenSendModal] = useState(false);
    const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);
    const [commissionStatementData, setCommissionStatementData] =
        useState<CommissionStatementDetails>();
    const [billingContactEmail, setBillingContactEmail] = useState<string>();

    const [getCommissionStatementPDF, { data: commissionPDF, loading: isLoadingPDF }] =
        useLazyQuery(GET_COMMISSION_STATEMENT_PDF, {
            fetchPolicy: 'cache-and-network',
            onError,
        });

    const { loading: loadingStatement, refetch: refetchStatementHistoryData } = useQuery(
        GET_COMMISSION_STATEMENT,
        {
            onError: (err) => {
                showErrorToast(err.message);
            },
            onCompleted: (data) => {
                const billingContactInfo = data?.commissionStatement?.billingContactInformation;

                setBillingContactEmail(billingContactInfo?.bci_email);

                const detailsData: CommissionStatementDetails = {
                    statementDetails: {
                        partnerName: data?.commissionStatement?.partnerName,
                        statementId: data?.commissionStatement?.commissionStatement?.id?.toString(),
                        statementDate:
                            data?.commissionStatement?.commissionStatement?.statementDate,
                        statementStatus: data?.commissionStatement?.commissionStatement
                            ?.status as StatementStatusType,
                    },
                    billingDetails: FormatBillingDetails(billingContactInfo),
                    totals: {
                        totalSetupAndAccessory: data?.commissionStatement?.totalSetupAndAccessory,
                        totalMonthly: data?.commissionStatement?.totalMonthly,
                        totalCommission: data?.commissionStatement?.totalCommission,
                    },
                    commissionItems:
                        data?.commissionStatement?.commissionStatement?.commissionItems,
                    currency: data?.commissionStatement?.currency,
                };

                setCommissionStatementData(detailsData);
            },
            variables: { id },
        }
    );

    const onOpenCloseSendModal = () => {
        setOpenSendModal(!openSendModal);
    };

    const onOpenClosePDFModal = () => {
        setIsPDFModalOpen(!isPDFModalOpen);
    };

    const commissionTypeHeading = getCommissionTypeHeading(commissionType);

    const emptyPdfBlob = new Blob([], { type: 'application/pdf' });
    const file = new File([emptyPdfBlob], `commission-statement-${id}.pdf`, {
        type: 'application/pdf',
    });

    const handleOpenModal = async () => {
        setIsPDFModalOpen(true);
        await getCommissionStatementPDF({
            variables: {
                statementId: parseInt(id),
            },
        });
    };

    if (loadingStatement) {
        return <Loader />;
    }

    return (
        <PageContent
            heading={`View ${commissionTypeHeading} Commission Statement`}
            callToActions={
                <>
                    <Button
                        variant="npi-primary"
                        startIcon={<BsEye />}
                        onClick={handleOpenModal}
                        sx={{
                            minWidth: '40px !important',
                            width: '40px',
                            height: '40px',
                            padding: '0px',
                            '& span': {
                                marginRight: '0px',
                                marginLeft: '0px',
                            },
                            boxShadow: 'none',
                        }}
                    />
                    <Button
                        variant="npi-primary"
                        startIcon={<PiPaperPlaneTilt />}
                        onClick={onOpenCloseSendModal}
                        sx={{
                            minWidth: '40px !important',
                            width: '40px',
                            height: '40px',
                            padding: '0px',
                            '& span': {
                                marginRight: '0px',
                                marginLeft: '0px',
                            },
                            boxShadow: 'none',
                        }}
                    />
                </>
            }
        >
            <MetaTags
                title={`View${commissionTypeHeading}Commission page`}
                description={`View${commissionTypeHeading}Commission page`}
            />

            <Grid container gap={3}>
                <>
                    <Grid display={'flex'} flexDirection={'row'} gap="24px" width={'100%'}>
                        <Box
                            display={'flex'}
                            flexDirection={'column'}
                            gap="24px"
                            flexBasis={'150%'}
                        >
                            <StatementDetails
                                statement={commissionStatementData?.statementDetails}
                            />
                            <Box display={'flex'} gap="19px" justifyContent={'space-between'}>
                                <OrderSumCard
                                    title="Total Setup And Accessory"
                                    value={
                                        commissionStatementData?.totals?.totalSetupAndAccessory || 0
                                    }
                                    usersCurrency={commissionStatementData?.currency}
                                    labelTypographyStyles={{
                                        fontSize: '18px',
                                        fontWeight: '600',
                                    }}
                                    valueTypographyStyles={{
                                        fontSize: '24px',
                                    }}
                                    containerStyles={{
                                        borderRadius: '16px',
                                        width: '100%',
                                    }}
                                    titleContainerStyles={{
                                        borderTopLeftRadius: '15px',
                                        borderTopRightRadius: '15px',
                                        height: '100%',
                                    }}
                                />
                                <OrderSumCard
                                    title={`Total ${commissionTypeHeading}`}
                                    value={commissionStatementData?.totals?.totalMonthly || 0}
                                    labelTypographyStyles={{
                                        fontSize: '18px',
                                        fontWeight: '600',
                                    }}
                                    valueTypographyStyles={{
                                        fontSize: '24px',
                                    }}
                                    containerStyles={{
                                        borderRadius: '16px',
                                        width: '100%',
                                    }}
                                    titleContainerStyles={{
                                        borderTopLeftRadius: '15px',
                                        borderTopRightRadius: '15px',
                                        height: '100%',
                                    }}
                                    usersCurrency={commissionStatementData?.currency}
                                />
                                <OrderSumCard
                                    title="Total Commission"
                                    value={commissionStatementData?.totals?.totalCommission || 0}
                                    labelTypographyStyles={{
                                        fontSize: '18px',
                                        fontWeight: '600',
                                    }}
                                    valueTypographyStyles={{
                                        fontSize: '24px',
                                    }}
                                    containerStyles={{
                                        borderRadius: '16px',
                                        width: '100%',
                                    }}
                                    titleContainerStyles={{
                                        borderTopLeftRadius: '15px',
                                        borderTopRightRadius: '15px',
                                        height: '100%',
                                    }}
                                    usersCurrency={commissionStatementData?.currency}
                                />
                            </Box>
                        </Box>
                        <BillingDetails
                            name={commissionStatementData?.billingDetails?.billingContact}
                            billingAddress={commissionStatementData?.billingDetails?.billingAddress}
                            contactNumber={commissionStatementData?.billingDetails?.contactNumber}
                            email={commissionStatementData?.billingDetails?.email}
                        />
                    </Grid>
                    <CommissionsItemsTable
                        commissionItems={commissionStatementData?.commissionItems}
                        commissionType={commissionType}
                        commissionTypeHeading={commissionTypeHeading}
                        currency={commissionStatementData?.currency}
                    />
                </>
            </Grid>
            {isLoadingPDF ? (
                <Loader />
            ) : (
                <ViewPDFModal
                    title="View Commission Statement PDF"
                    file={file}
                    fileURL={commissionPDF?.getCommissionStatementPDF?.downloadLink}
                    isModalOpen={isPDFModalOpen}
                    maxWidth="lg"
                    setIsModalOpen={onOpenClosePDFModal}
                />
            )}
            <Dialog
                open={openSendModal}
                onClose={onOpenCloseSendModal}
                aria-labelledby="send-pdf-modal"
                sx={{
                    '& .MuiPaper-root': {
                        margin: '0px',
                        padding: '0px',
                        borderRadius: '16px',
                    },
                }}
            >
                <Box
                    sx={{
                        background: 'linear-gradient(90deg, #FF8008, #FFC837)',
                        height: '82px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '24px',
                    }}
                >
                    <Typography color="white" variant="h2">
                        Resend Statement
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<BsX width="24px" height="24px" />}
                        onClick={onOpenCloseSendModal}
                        sx={{
                            color: '#F39E4E',
                            minWidth: '32px !important',
                            width: '32px',
                            height: '32px',
                            padding: '0px',
                            borderRadius: '50%',
                            backgroundColor: 'white',
                            border: '1px solid #F39E4E',
                            '& span': {
                                marginRight: '0px',
                                marginLeft: '0px',
                            },
                            '&:hover': {
                                backgroundColor: '#eee !important',
                            },
                            boxShadow: 'none',
                        }}
                    />
                </Box>
                <DialogContent>
                    <SendCommissionStatementModal
                        handleCloseSendModal={onOpenCloseSendModal}
                        statementId={id}
                        refetchStatementHistoryData={refetchStatementHistoryData}
                        partnerBillingContactEmail={billingContactEmail}
                    />
                </DialogContent>
            </Dialog>
        </PageContent>
    );
};

export default ViewCommissionStatementPage;
